/**
 * Pure helpers of the Creator library in the portal — no Angular, so the spec
 * runs without a TestBed. Same rules as canvas `src/lib/creator-items.ts`,
 * which the canvas start page used before the portal took it over.
 */
import { ItemSummary, LiveTarget, LockInfo } from '../../services/creator-library.service';

/** "vor 5 Minuten", "gestern", … in the given locale. */
export function relativeTime(iso: string, now: number, locale: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const seconds = Math.round((then - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 3600],
    ['month', 30 * 24 * 3600],
    ['week', 7 * 24 * 3600],
    ['day', 24 * 3600],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, 'minute');
}

/** A live lock held by someone else (an own lock is no news). */
export function foreignLock(item: ItemSummary, userId: string | undefined, now: number): LockInfo | undefined {
  const lock = item.lock;
  if (!lock || lock.by.id === userId) return undefined;
  const until = Date.parse(lock.until);
  return Number.isFinite(until) && until > now ? lock : undefined;
}

export interface LiveLabel { text: string; outdated: boolean; failed: boolean; }

/**
 * "Live in": one label per target and version in the order they come; several
 * buckets on the same version are counted ("Wissensbasis v2 (2)").
 */
export function liveLabels(targets: readonly LiveTarget[] | undefined, names: Readonly<Record<string, string>>): LiveLabel[] {
  const groups = new Map<string, { target: string; versionNo: number; count: number; outdated: boolean; failed: boolean }>();
  for (const t of targets ?? []) {
    const key = `${t.target}:${t.versionNo}`;
    const group = groups.get(key) ?? { target: t.target, versionNo: t.versionNo, count: 0, outdated: false, failed: false };
    group.count += 1;
    group.outdated = group.outdated || t.outdated;
    group.failed = group.failed || t.status === 'failed';
    groups.set(key, group);
  }
  return Array.from(groups.values()).map((g) => ({
    text: `${names[g.target] ?? g.target} v${g.versionNo}${g.count > 1 ? ` (${g.count})` : ''}`,
    outdated: g.outdated,
    failed: g.failed,
  }));
}

/** "erstellt von X" only when someone else made it than the last change names. */
export function createdByOther(item: Pick<ItemSummary, 'createdBy' | 'updatedBy'>): string | null {
  const created = item.createdBy;
  if (!created?.name) return null;
  return item.updatedBy?.id === created.id ? null : created.name;
}

/** Pages of `size` for `total` rows, at least one. */
export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / size));
}
